import React from 'react';
import env from '../../env';

/**
 * ItemPageSchema Component
 * 
 * Implements Schema.org ItemPage structured data for product detail pages.
 * Wraps the existing Product schema as mainEntity.
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/ItemPage
 * 
 * @param {Object} props
 * @param {string} props.name - Page title
 * @param {string} props.description - Page description
 * @param {string} props.url - Page URL (relative or absolute)
 * @param {Object} props.breadcrumb - Breadcrumb data (optional)
 * @param {Object} props.mainEntity - Product schema object (optional)
 * @param {string} props.image - Page/product image URL (optional)
 * 
 * @example
 * <ItemPageSchema
 *   name="Hair Ties | Scunci"
 *   description="Premium hair ties for all hair types"
 *   url="/ecm/products/hair-ties"
 *   breadcrumb={breadcrumbData}
 *   image="/images/product.jpg"
 * />
 */
export function ItemPageSchema({ 
  name, 
  description, 
  url, 
  breadcrumb,
  mainEntity,
  image 
}) {
  React.useEffect(() => {
    // Validate required data
    if (!name || !url) {
      return;
    }

    // Remove any existing item page schema script
    const existingScript = document.querySelector('script[data-item-page-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Build absolute URL
    let absoluteUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      const normalizedUrl = url.startsWith('/') ? url : '/' + url;
      absoluteUrl = env.baseOrigin + normalizedUrl;
    }

    // Build absolute image URL if provided
    let absoluteImageUrl = null;
    if (image) {
      if (image.startsWith('http://') || image.startsWith('https://')) {
        absoluteImageUrl = image;
      } else {
        const normalizedImage = image.startsWith('/') ? image : '/' + image;
        absoluteImageUrl = env.baseOrigin + normalizedImage;
      }
    }

    // Build ItemPage schema according to Schema.org specification
    const itemPageSchema = {
      "@context": "https://schema.org/",
      "@type": "ItemPage",
      "@id": absoluteUrl,
      "name": name,
      "url": absoluteUrl
    };

    // Add description if provided
    if (description) {
      itemPageSchema.description = description;
    }

    // Add image if provided
    if (absoluteImageUrl) {
      itemPageSchema.image = absoluteImageUrl;
    }

    // Add breadcrumb reference if provided
    // Note: Breadcrumb is injected separately via BreadcrumbSchema component
    // This just references it by ID for proper linking
    if (breadcrumb && breadcrumb.items && breadcrumb.items.length > 0) {
      itemPageSchema.breadcrumb = {
        "@id": absoluteUrl + "#breadcrumb"
      };
    }

    // Add mainEntity (Product) if provided
    // Note: Product schema is injected separately, this references it
    if (mainEntity) {
      itemPageSchema.mainEntity = mainEntity;
    }

    // Create and inject JSON-LD script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-item-page-schema', 'true');
    script.textContent = JSON.stringify(itemPageSchema, null, 0);
    document.head.appendChild(script);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const scriptToRemove = document.querySelector('script[data-item-page-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, url, breadcrumb, mainEntity, image]); // Re-run if any prop changes

  // Return null since we're using direct DOM manipulation
  return null;
}
